// Setup home (`/c/{slug}/setup`; SETUP-REGROUP, review #18): what used to redirect to General is
// now the map of Setup, the way Linear's and Notion's settings open — a search box at the top,
// then one card per group (Company, People & access, Structure, Measurement, Finance, Workforce,
// Integrations, Security & audit), each page a row. UX-A (0.8): the cards list Setup pages only;
// a page in another mode (Skill packs, Priorities) or a tab's other name (Connection checks) is
// found by the search, a mode's page marked ↗. Labels stay short: a group's and a page's
// description sit behind an (i). The search matches labels, descriptions, group names and old
// names ("roles", "audit"); no call is made.
import { cn, EmptyState, InfoTip, SearchField } from "@companyos/ui";
import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon, ChevronRightIcon, SearchIcon } from "lucide-react";
import { useState } from "react";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { useFeature } from "../use-feature";
import {
  isListed,
  MODE_LABEL,
  matchSetup,
  SETUP_GROUPS,
  type SetupEntry,
  setupEntries,
} from "./setup-catalog";
import { SetupPage } from "./setup-page";

export function SetupHome({ company }: { company: CompanyAccess }) {
  const kpi = useFeature("kpi").enabled;
  const memory = useFeature("memory").enabled;
  const [query, setQuery] = useState("");
  const all = setupEntries({ kpi, memory });
  const shown = query.trim() ? matchSetup(all, query) : all.filter(isListed);
  return (
    <SetupPage title="Settings" slot="setup-home">
      <div className="flex items-center gap-2">
        <SearchField
          className="w-full max-w-[420px]"
          value={query}
          placeholder="Search settings…"
          aria-label="Search settings"
          onChange={(e) => setQuery(e.target.value)}
        />
        <InfoTip label="About Settings">
          The company's settings, by area. Your own settings (appearance, notifications, your keys)
          are in the account menu under Your settings.
        </InfoTip>
      </div>
      {shown.length === 0 ? (
        <EmptyState
          variant="compact"
          icon={SearchIcon}
          title="No setting matches"
          description={`Nothing in Setup matches "${query.trim()}".`}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2" data-slot="setup-groups">
          {SETUP_GROUPS.map((g) => {
            const rows = shown.filter((e) => e.group === g.key);
            if (!rows.length) return null;
            const headingId = `setup-group-${g.key}`;
            return (
              <section
                key={g.key}
                aria-labelledby={headingId}
                data-slot="setup-group"
                data-group={g.key}
                className="overflow-hidden rounded-lg border border-border bg-card text-card-foreground"
              >
                <div className="flex items-center gap-2.5 border-b border-subtle px-4 py-2.5">
                  <g.icon aria-hidden="true" className="size-4 shrink-0 text-faint" />
                  <h2 id={headingId} className="text-[13px] font-semibold text-foreground">
                    {g.label}
                  </h2>
                  <InfoTip label={`About ${g.label}`}>{g.description}</InfoTip>
                </div>
                <ul className="flex flex-col">
                  {rows.map((e) => (
                    <li
                      key={e.key}
                      className="flex items-center border-t border-subtle pr-2 first:border-t-0 hover:bg-muted"
                    >
                      <EntryLink entry={e} slug={company.slug} />
                      <InfoTip label={`About ${e.label}`}>{e.description}</InfoTip>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </SetupPage>
  );
}

function EntryLink({ entry, slug }: { entry: SetupEntry; slug: string }) {
  const Trail = entry.mode ? ArrowUpRightIcon : ChevronRightIcon;
  return (
    <Link
      to={entry.to as never}
      params={{ slug } as never}
      search={
        ((prev: Record<string, unknown>) => ({ ...keepFocus(prev), ...entry.search })) as never
      }
      {...(entry.hash ? { hash: entry.hash } : {})}
      data-entry={entry.key}
      className={cn(
        "group/entry flex min-h-10 min-w-0 flex-1 items-center gap-3 px-4 py-2 outline-none",
        "focus-visible:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
      )}
    >
      <entry.icon aria-hidden="true" className="size-4 shrink-0 text-faint" />
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">
        {entry.label}
        {entry.mode ? <span className="sr-only"> (opens {MODE_LABEL[entry.mode]})</span> : null}
      </span>
      <Trail
        aria-hidden="true"
        className="size-4 shrink-0 text-faint group-hover/entry:text-secondary"
      />
    </Link>
  );
}
